/**
 * Life Blueprint V2.9.9 — M02 ReferenceData · Controlled Batch 01
 *   (DB_天干 — Sheet40 A1:D11, DB_地支 — Sheet41 A1:E13)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * STATUS
 *   • DB_天干 dataset status = AUTHORITATIVE_DATASET_COMPLETE (10 rows)
 *   • DB_地支 dataset status = AUTHORITATIVE_DATASET_COMPLETE (12 rows)
 *   • M02 overall: IN_PROGRESS, referenceCoverage = PARTIAL
 *     (only these two tables migrated; all other DB tables remain
 *     NOT_STARTED per `v299Manifest.js`).
 *
 * ISOLATION
 *   This slice does NOT write into `src/engine/referenceData.js`.
 *   The production ReferenceData registry continues to hold only the
 *   MOCK_POC architectural placeholders. Composition into the
 *   production registry is a later, separately-scoped task performed
 *   only when the main V2.9.9 pipeline is being wired.
 *
 * UNICODE POLICY
 *   Every Chinese literal is built from its audited code point via
 *   `String.fromCodePoint(...)`. Direct Chinese glyphs are NOT used in
 *   code; a comment on each entry states the canonical glyph for reader
 *   convenience. This preserves the MIGRATION_RULES.md → Encoding
 *   Warning contract byte-for-byte.
 *
 * LOOKUP CONTRACT
 *   Exact-match Unicode-safe equality. No fuzzy match, no
 *   transliteration, no silent substitution. Missing keys throw
 *   `MISSING_REFERENCE_KEY` — a NEW error code distinct from the M03
 *   `MISSING_REFERENCE_EVIDENCE` code used by partial-audit slices,
 *   because these datasets are now COMPLETE (any missing key is a
 *   caller error, not an audit gap).
 *
 * DETERMINISM
 *   No wall clock, no randomness, no LLM. Byte-wise identical output
 *   on every run.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Character constants (Unicode-safe, one per audited glyph) ────────────────

// 天干 (Heavenly Stems)
const CH_JIA  = String.fromCodePoint(0x7532); // 甲
const CH_YI   = String.fromCodePoint(0x4E59); // 乙
const CH_BING = String.fromCodePoint(0x4E19); // 丙
const CH_DING = String.fromCodePoint(0x4E01); // 丁
const CH_WU   = String.fromCodePoint(0x620A); // 戊
const CH_JI   = String.fromCodePoint(0x5DF1); // 己
const CH_GENG = String.fromCodePoint(0x5E9A); // 庚
const CH_XIN  = String.fromCodePoint(0x8F9B); // 辛
const CH_REN  = String.fromCodePoint(0x58EC); // 壬
const CH_GUI  = String.fromCodePoint(0x7678); // 癸

// 地支 (Earthly Branches)
const CH_ZI_BRANCH  = String.fromCodePoint(0x5B50); // 子
const CH_CHOU       = String.fromCodePoint(0x4E11); // 丑
const CH_YIN_BRANCH = String.fromCodePoint(0x5BC5); // 寅
const CH_MAO        = String.fromCodePoint(0x536F); // 卯
const CH_CHEN       = String.fromCodePoint(0x8FB0); // 辰
const CH_SI_BRANCH  = String.fromCodePoint(0x5DF3); // 巳
const CH_WU_BRANCH  = String.fromCodePoint(0x5348); // 午
const CH_WEI        = String.fromCodePoint(0x672A); // 未
const CH_SHEN       = String.fromCodePoint(0x7533); // 申
const CH_YOU        = String.fromCodePoint(0x9149); // 酉
const CH_XU         = String.fromCodePoint(0x620C); // 戌
const CH_HAI        = String.fromCodePoint(0x4EA5); // 亥

// 五行 (Five Elements)
const EL_WOOD  = String.fromCodePoint(0x6728); // 木
const EL_FIRE  = String.fromCodePoint(0x706B); // 火
const EL_EARTH = String.fromCodePoint(0x571F); // 土
const EL_METAL = String.fromCodePoint(0x91D1); // 金
const EL_WATER = String.fromCodePoint(0x6C34); // 水

// 阴阳 (Yin/Yang)
const YY_YANG = String.fromCodePoint(0x9633); // 阳
const YY_YIN  = String.fromCodePoint(0x9634); // 阴

// 生肖 (Zodiac animals — 地支 only)
const ZO_RAT     = String.fromCodePoint(0x9F20); // 鼠
const ZO_OX      = String.fromCodePoint(0x725B); // 牛
const ZO_TIGER   = String.fromCodePoint(0x864E); // 虎
const ZO_RABBIT  = String.fromCodePoint(0x5154); // 兔
const ZO_DRAGON  = String.fromCodePoint(0x9F99); // 龙
const ZO_SNAKE   = String.fromCodePoint(0x86C7); // 蛇
const ZO_HORSE   = String.fromCodePoint(0x9A6C); // 马
const ZO_GOAT    = String.fromCodePoint(0x7F8A); // 羊
const ZO_MONKEY  = String.fromCodePoint(0x7334); // 猴
const ZO_ROOSTER = String.fromCodePoint(0x9E21); // 鸡
const ZO_DOG     = String.fromCodePoint(0x72D7); // 狗
const ZO_PIG     = String.fromCodePoint(0x732A); // 猪

// ── Excel header labels (audited Unicode) ────────────────────────────────────
const HEADER_XU_HAO      = String.fromCodePoint(0x5E8F, 0x53F7); // 序号
const HEADER_TIAN_GAN    = String.fromCodePoint(0x5929, 0x5E72); // 天干
const HEADER_WU_XING     = String.fromCodePoint(0x4E94, 0x884C); // 五行
const HEADER_YIN_YANG    = String.fromCodePoint(0x9634, 0x9633); // 阴阳
const HEADER_DI_ZHI      = String.fromCodePoint(0x5730, 0x652F); // 地支
const HEADER_BEN_QI_WX   = String.fromCodePoint(0x672C, 0x6C14, 0x4E94, 0x884C); // 本气五行
const HEADER_SHENG_XIAO  = String.fromCodePoint(0x751F, 0x8096); // 生肖

// ── DB_天干 dataset (Sheet40, A1:D11) ────────────────────────────────────────

/**
 * DB_天干 — 10 heavenly stems. Frozen, canonical, audited.
 *
 * Excel source: Sheet40 (CodeName), sheet display name = `DB_天干`
 * Used range: `$A$1:$D$11` (header row + 10 data rows).
 */
export const DB_TIAN_GAN = Object.freeze({
  codeName: "Sheet40",
  worksheetIndex: 40,
  sheetName: String.fromCodePoint(0x44, 0x42, 0x5F, 0x5929, 0x5E72), // DB_天干
  sheetNameUnicode: [68, 66, 95, 22825, 24178],
  excelUsedRange: "A1:D11",
  status: "AUTHORITATIVE_DATASET_COMPLETE",
  evidenceProvenance: "read-only Golden Master extraction",
  auditedInBatch: "M02_BATCH_01",
  headers: Object.freeze({
    A: HEADER_XU_HAO,       // 序号
    B: HEADER_TIAN_GAN,     // 天干
    C: HEADER_WU_XING,      // 五行
    D: HEADER_YIN_YANG,     // 阴阳
  }),
  rows: Object.freeze([
    Object.freeze({ xuHao: 1,  tianGan: CH_JIA,  wuXing: EL_WOOD,  yinYang: YY_YANG }), // 甲 木 阳
    Object.freeze({ xuHao: 2,  tianGan: CH_YI,   wuXing: EL_WOOD,  yinYang: YY_YIN  }), // 乙 木 阴
    Object.freeze({ xuHao: 3,  tianGan: CH_BING, wuXing: EL_FIRE,  yinYang: YY_YANG }), // 丙 火 阳
    Object.freeze({ xuHao: 4,  tianGan: CH_DING, wuXing: EL_FIRE,  yinYang: YY_YIN  }), // 丁 火 阴
    Object.freeze({ xuHao: 5,  tianGan: CH_WU,   wuXing: EL_EARTH, yinYang: YY_YANG }), // 戊 土 阳
    Object.freeze({ xuHao: 6,  tianGan: CH_JI,   wuXing: EL_EARTH, yinYang: YY_YIN  }), // 己 土 阴
    Object.freeze({ xuHao: 7,  tianGan: CH_GENG, wuXing: EL_METAL, yinYang: YY_YANG }), // 庚 金 阳
    Object.freeze({ xuHao: 8,  tianGan: CH_XIN,  wuXing: EL_METAL, yinYang: YY_YIN  }), // 辛 金 阴
    Object.freeze({ xuHao: 9,  tianGan: CH_REN,  wuXing: EL_WATER, yinYang: YY_YANG }), // 壬 水 阳
    Object.freeze({ xuHao: 10, tianGan: CH_GUI,  wuXing: EL_WATER, yinYang: YY_YIN  }), // 癸 水 阴
  ]),
});

// ── DB_地支 dataset (Sheet41, A1:E13) ────────────────────────────────────────

/**
 * DB_地支 — 12 earthly branches. Frozen, canonical, audited.
 *
 * Excel source: Sheet41 (CodeName), sheet display name = `DB_地支`
 * Used range: `$A$1:$E$13` (header row + 12 data rows).
 */
export const DB_DI_ZHI = Object.freeze({
  codeName: "Sheet41",
  worksheetIndex: 41,
  sheetName: String.fromCodePoint(0x44, 0x42, 0x5F, 0x5730, 0x652F), // DB_地支
  sheetNameUnicode: [68, 66, 95, 22320, 25903],
  excelUsedRange: "A1:E13",
  status: "AUTHORITATIVE_DATASET_COMPLETE",
  evidenceProvenance: "read-only Golden Master extraction",
  auditedInBatch: "M02_BATCH_01",
  headers: Object.freeze({
    A: HEADER_XU_HAO,      // 序号
    B: HEADER_DI_ZHI,      // 地支
    C: HEADER_BEN_QI_WX,   // 本气五行
    D: HEADER_YIN_YANG,    // 阴阳
    E: HEADER_SHENG_XIAO,  // 生肖
  }),
  rows: Object.freeze([
    Object.freeze({ xuHao: 1,  diZhi: CH_ZI_BRANCH,  benQiWuXing: EL_WATER, yinYang: YY_YANG, shengXiao: ZO_RAT     }), // 子 水 阳 鼠
    Object.freeze({ xuHao: 2,  diZhi: CH_CHOU,       benQiWuXing: EL_EARTH, yinYang: YY_YIN,  shengXiao: ZO_OX      }), // 丑 土 阴 牛
    Object.freeze({ xuHao: 3,  diZhi: CH_YIN_BRANCH, benQiWuXing: EL_WOOD,  yinYang: YY_YANG, shengXiao: ZO_TIGER   }), // 寅 木 阳 虎
    Object.freeze({ xuHao: 4,  diZhi: CH_MAO,        benQiWuXing: EL_WOOD,  yinYang: YY_YIN,  shengXiao: ZO_RABBIT  }), // 卯 木 阴 兔
    Object.freeze({ xuHao: 5,  diZhi: CH_CHEN,       benQiWuXing: EL_EARTH, yinYang: YY_YANG, shengXiao: ZO_DRAGON  }), // 辰 土 阳 龙
    Object.freeze({ xuHao: 6,  diZhi: CH_SI_BRANCH,  benQiWuXing: EL_FIRE,  yinYang: YY_YIN,  shengXiao: ZO_SNAKE   }), // 巳 火 阴 蛇
    Object.freeze({ xuHao: 7,  diZhi: CH_WU_BRANCH,  benQiWuXing: EL_FIRE,  yinYang: YY_YANG, shengXiao: ZO_HORSE   }), // 午 火 阳 马
    Object.freeze({ xuHao: 8,  diZhi: CH_WEI,        benQiWuXing: EL_EARTH, yinYang: YY_YIN,  shengXiao: ZO_GOAT    }), // 未 土 阴 羊
    Object.freeze({ xuHao: 9,  diZhi: CH_SHEN,       benQiWuXing: EL_METAL, yinYang: YY_YANG, shengXiao: ZO_MONKEY  }), // 申 金 阳 猴
    Object.freeze({ xuHao: 10, diZhi: CH_YOU,        benQiWuXing: EL_METAL, yinYang: YY_YIN,  shengXiao: ZO_ROOSTER }), // 酉 金 阴 鸡
    Object.freeze({ xuHao: 11, diZhi: CH_XU,         benQiWuXing: EL_EARTH, yinYang: YY_YANG, shengXiao: ZO_DOG     }), // 戌 土 阳 狗
    Object.freeze({ xuHao: 12, diZhi: CH_HAI,        benQiWuXing: EL_WATER, yinYang: YY_YIN,  shengXiao: ZO_PIG     }), // 亥 水 阴 猪
  ]),
});

// ── Lookup API ───────────────────────────────────────────────────────────────

/**
 * Internal helper — throw `MISSING_REFERENCE_KEY` with a full context
 * payload. Missing keys must always be visible (never silently return
 * a default / substituted glyph).
 */
function throwMissingKey(datasetName, columnName, key) {
  const err = new Error(
    `[M02·${datasetName}] key ${JSON.stringify(key)} not found in ` +
      `AUTHORITATIVE_DATASET_COMPLETE. No fuzzy match or substitution ` +
      `is performed.`
  );
  err.code = "MISSING_REFERENCE_KEY";
  err.dataset = datasetName;
  err.column = columnName;
  err.key = key;
  throw err;
}

/**
 * VLOOKUP(stem, DB_天干!B:D, 2, FALSE) semantics.
 * Returns the 五行 (Five Element) for the given stem via exact-match
 * Unicode-safe equality.
 *
 * @param {string} stem — audited 天干 glyph (one code point, exact)
 * @returns {string}
 */
export function tianGanWuXing(stem) {
  if (typeof stem !== "string") {
    throw new TypeError("[M02·DB_天干] stem must be a string");
  }
  const row = DB_TIAN_GAN.rows.find((r) => r.tianGan === stem);
  if (!row) throwMissingKey("DB_天干", "wuXing", stem);
  return row.wuXing;
}

/**
 * VLOOKUP(stem, DB_天干!B:D, 3, FALSE) semantics.
 * Returns the 阴阳 for the given stem.
 */
export function tianGanYinYang(stem) {
  if (typeof stem !== "string") {
    throw new TypeError("[M02·DB_天干] stem must be a string");
  }
  const row = DB_TIAN_GAN.rows.find((r) => r.tianGan === stem);
  if (!row) throwMissingKey("DB_天干", "yinYang", stem);
  return row.yinYang;
}

/**
 * VLOOKUP(branch, DB_地支!B:E, 2, FALSE) semantics.
 * Returns the 本气五行 for the given branch.
 */
export function diZhiBenQiWuXing(branch) {
  if (typeof branch !== "string") {
    throw new TypeError("[M02·DB_地支] branch must be a string");
  }
  const row = DB_DI_ZHI.rows.find((r) => r.diZhi === branch);
  if (!row) throwMissingKey("DB_地支", "benQiWuXing", branch);
  return row.benQiWuXing;
}

/**
 * VLOOKUP(branch, DB_地支!B:E, 3, FALSE) semantics.
 * Returns the 阴阳 for the given branch.
 */
export function diZhiYinYang(branch) {
  if (typeof branch !== "string") {
    throw new TypeError("[M02·DB_地支] branch must be a string");
  }
  const row = DB_DI_ZHI.rows.find((r) => r.diZhi === branch);
  if (!row) throwMissingKey("DB_地支", "yinYang", branch);
  return row.yinYang;
}

/**
 * VLOOKUP(branch, DB_地支!B:E, 4, FALSE) semantics.
 * Returns the 生肖 for the given branch.
 *
 * Preserved because DB_地支!E is part of the audited dataset even though
 * Sheet27 B79:E81 do not currently consume this column.
 */
export function diZhiShengXiao(branch) {
  if (typeof branch !== "string") {
    throw new TypeError("[M02·DB_地支] branch must be a string");
  }
  const row = DB_DI_ZHI.rows.find((r) => r.diZhi === branch);
  if (!row) throwMissingKey("DB_地支", "shengXiao", branch);
  return row.shengXiao;
}
